import React from 'react';
import { ButtonV2, Flex, JoyKitThemeProvider, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { closeButtonOverridesV2, dialogOverridesV2, drawerOverridesV2 } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useHotelBookingGuestSiteTelemetry } from '../../HotelBooking.telemetry';
import { TravelCurrencyCode } from '@graphql/generated';

interface RedirectInterceptorDialogProps {
  pageContext: string;
  isOpen: boolean;
  hotel?: {
    hotelName?: string | null;
    hotelPriceCurrency?: TravelCurrencyCode | null;
    hotelPrice?: string | null;
    hotelStrikeOutPrice?: string | null;
  };
  redirectUrls?: {
    partnerSearch: string;
    partnerHotel?: string;
  };
  onDecline: () => void;
  onAccept: () => void;
}

export const RedirectInterceptorDialog = ({
  pageContext,
  isOpen,
  hotel,
  onAccept: onAcceptCallback,
  onDecline: onDeclineCallback,
  redirectUrls,
}: RedirectInterceptorDialogProps) => {
  const { t } = useTranslation('guestSiteTravel');
  const interceptorRedirectDialogTrans = t('interceptorRedirectDialog');
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  const telemetry = useHotelBookingGuestSiteTelemetry();
  const isAccommodationPlacePartner = pageContext === 'AccommodationPlacePartner';

  const onAccept = useEventCallback(() => {
    if (isAccommodationPlacePartner) {
      telemetry.accommodationPlaceSoldOutDialog({ dialogAction: 'See nearby hotels', ...hotel });
    } else {
      telemetry.partnerHotelRedirectInterceptorDialog({ dialogAction: 'accepted', ...hotel });
    }
    onAcceptCallback();
  });

  const onDecline = useEventCallback(() => {
    if (isAccommodationPlacePartner) {
      telemetry.accommodationPlaceSoldOutDialog({ dialogAction: 'Back to Hosts Hotel List', ...hotel });
    } else {
      telemetry.partnerHotelRedirectInterceptorDialog({ dialogAction: 'declined', ...hotel });
    }
    onDeclineCallback();
  });

  return (
    <JoyKitThemeProvider>
      <ResponsiveDialog
        dialogOverrides={{ ...dialogOverridesV2, Content: { props: { maxWidth: pxToRem(540) } }, Footer: { props: { paddingX: 80 } } }}
        drawerOverrides={drawerOverridesV2}
        closeButtonOverrides={closeButtonOverridesV2}
        isOpen={isOpen}
        hasCloseButton={!isMobile}
        onClose={onDecline}
        hideDivider={true}
        removeBodyPadding={true}
        hideFooterDivider={!isMobile}
        disableCloseOnEscapeKeyClick
        disableCloseOnOutsideClick
        renderFooter={() => {
          return (
            <Flex
              flexDirection="column"
              width="100%"
              gap={5}
              justifyContent="center"
            >
              <ButtonV2
                typographyVariant="button2"
                width="100%"
                shape="rounded"
                intent="neutral"
                onClick={onAccept}
                {...(redirectUrls?.partnerSearch && { as: 'a', href: redirectUrls.partnerSearch, target: '_blank' })}
              >
                {isAccommodationPlacePartner ? interceptorRedirectDialogTrans.seeNearbyHotels() : interceptorRedirectDialogTrans.acceptCta()}
              </ButtonV2>
              <ButtonV2
                typographyVariant="button2"
                width="100%"
                color="mono14"
                variant="ghost"
                onClick={onDecline}
                {...(redirectUrls?.partnerHotel && { as: 'a', href: redirectUrls.partnerHotel, target: '_blank' })}
              >
                {isAccommodationPlacePartner ? interceptorRedirectDialogTrans.backToHostsHotelList() : interceptorRedirectDialogTrans.declineCta()}
              </ButtonV2>
            </Flex>
          );
        }}
      >
        <Flex
          flexDirection={'column'}
          textAlign={'center'}
          paddingX={{ _: 6, sm2: 8 }}
          paddingTop={{ _: 7, sm2: 9 }}
          paddingBottom={{ _: 7, sm2: 5 }}
        >
          <TextV2
            typographyVariant={'hed4'}
            color={'#020202'}
          >
            {hotel?.hotelName ? interceptorRedirectDialogTrans.titleWithHotelName({ hotelName: hotel.hotelName }) : interceptorRedirectDialogTrans.title()}
          </TextV2>
          <TextV2
            typographyVariant={'body1'}
            color={'mono10'}
            marginTop={3}
            marginBottom={{ _: 6, sm2: 0 }}
          >
            {isAccommodationPlacePartner ? interceptorRedirectDialogTrans.accommodationPlaceContent() : interceptorRedirectDialogTrans.content()}
          </TextV2>
        </Flex>
      </ResponsiveDialog>
    </JoyKitThemeProvider>
  );
};
